import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  User,
  Trophy,
  Flame,
  Dumbbell,
  MonitorPlay,
  SquarePen,
  BarChart3,
  Activity,
  BadgePoundSterling,
  UserCog,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { isAdminRole, isSuperadminRole } from "../../context/authUser";
import { GymFeatureKey, gymConfig } from "../../config/gymConfig";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  danger?: boolean;
  adminOnly?: boolean;
  opsOnly?: boolean;
  feature?: GymFeatureKey;
};

const baseNavItems: NavItem[] = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/training", label: "Training", icon: Dumbbell, feature: "trainingLogs" },
  { to: "/profile", label: "Profile", icon: User, feature: "profile" },
  { to: "/leaderboard", label: "Board of Fame", icon: Trophy, feature: "leaderboard" },
  { to: "/board-of-shame", label: "Board of Shame", icon: Flame, danger: true, feature: "boardOfShame" },
];

const adminNavItems: NavItem[] = [
  { to: "/admin/insights", label: "Insights", icon: BarChart3, adminOnly: true, feature: "adminInsights" },
  { to: "/admin/performance", label: "Performance", icon: Activity, adminOnly: true, feature: "adminPerformance" },
  { to: "/display", label: "Display", icon: MonitorPlay, adminOnly: true, feature: "ptProgramming" },
  { to: "/editor", label: "Editor", icon: SquarePen, adminOnly: true, feature: "ptProgramming" },
];

const opsNavItems: NavItem[] = [
  { to: "/ops/staff", label: "Staff", icon: UserCog, opsOnly: true },
  { to: "/ops/memberships", label: "Memberships", icon: BadgePoundSterling, opsOnly: true },
  { to: "/ops/timetable", label: "Timetable", icon: CalendarDays, opsOnly: true },
];

function getNavTone({
  danger,
  adminOnly,
  opsOnly,
  isActive,
}: {
  danger?: boolean;
  adminOnly?: boolean;
  opsOnly?: boolean;
  isActive: boolean;
}) {
  if (danger) {
    return isActive
      ? "border-[color:var(--status-danger-border)] text-[color:var(--status-danger-text)]"
      : "border-transparent text-[color:var(--status-danger-text)] hover:border-[color:var(--status-danger-border)]/60 hover:text-[color:var(--color-page-text)]";
  }

  if (opsOnly) {
    return isActive
      ? "border-[color:rgba(136,156,255,0.32)] text-[color:#dbe6ff]"
      : "border-transparent text-[color:rgba(203,213,255,0.72)] hover:border-[color:rgba(136,156,255,0.22)] hover:text-[color:#dbe6ff]";
  }

  if (adminOnly) {
    return isActive
      ? "border-[color:rgba(136,156,255,0.32)] text-[color:#dbe6ff]"
      : "border-transparent text-[color:rgba(203,213,255,0.72)] hover:border-[color:rgba(136,156,255,0.22)] hover:text-[color:#dbe6ff]";
  }

  return isActive
    ? "border-[color:rgba(136,156,255,0.32)] text-white"
    : "border-transparent text-[color:var(--color-text-muted)] hover:border-[color:rgba(136,156,255,0.18)] hover:text-white";
}

export default function UserTopNav() {
  const { appUser } = useAuth();
  const isAdmin = isAdminRole(appUser?.role);
  const isSuperadmin = isSuperadminRole(appUser?.role);

  const navItems = [
    ...baseNavItems,
    ...(isAdmin ? adminNavItems : []),
    ...(isSuperadmin ? opsNavItems : []),
  ].filter((item) => !item.feature || gymConfig.features[item.feature]);

  return (
    <div className="sticky top-0 z-30 border-b border-[color:rgba(136,156,255,0.10)] bg-[linear-gradient(180deg,rgba(7,9,15,0.95),rgba(5,6,9,0.92))] backdrop-blur-2xl">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex gap-2 overflow-x-auto py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {navItems.map(({ to, label, icon: Icon, danger, adminOnly, opsOnly }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                [
                  "group inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2 text-sm font-semibold transition",
                  getNavTone({ danger, adminOnly, opsOnly, isActive }),
                ].join(" ")
              }
            >
              <Icon className="h-4 w-4 transition group-hover:scale-[1.03]" />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}
